import React, { useState, useEffect } from 'react';
import { Bell, X, MapPin, Clock, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * NewShiftAlert - Flash Banner for new shift assignments
 * 
 * Displays when officer receives a new shift assignment
 * Auto-hides after 10 seconds or can be dismissed manually
 */
export function NewShiftAlert({ shifts, onDismiss }) {
  const [visible, setVisible] = useState(false);
  const [currentShift, setCurrentShift] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (shifts && shifts.length > 0) {
      setCurrentShift(shifts[0]);
      setVisible(true);

      // Auto-hide after 10 seconds
      const timer = setTimeout(() => {
        handleDismiss();
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [shifts]);

  const handleDismiss = () => {
    setVisible(false);
    if (onDismiss) {
      onDismiss();
    }
  };

  const handleViewShift = () => {
    handleDismiss();
    navigate('/my-shifts');
  };

  if (!visible || !currentShift) return null;

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    try {
      const date = new Date(timeStr);
      return date.toLocaleTimeString('en-AU', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
    } catch {
      return timeStr;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-AU', { 
        weekday: 'short',
        day: 'numeric',
        month: 'short'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div 
      className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-in slide-in-from-top-4 duration-300"
      data-testid="new-shift-alert"
    >
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-lg shadow-2xl border border-emerald-500/50 overflow-hidden min-w-[400px] max-w-[500px]">
        {/* Header with pulsing indicator */}
        <div className="flex items-center justify-between px-4 py-2 bg-emerald-800/30">
          <div className="flex items-center gap-2">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
            </span>
            <Bell className="w-4 h-4" />
            <span className="font-bold text-sm uppercase tracking-wider">NEW SHIFT ASSIGNED</span>
          </div>
          <button 
            onClick={handleDismiss}
            className="text-white/70 hover:text-white transition-colors"
            data-testid="dismiss-shift-alert"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-lg truncate">
                {currentShift.site_name || currentShift.site?.name || 'New Shift'}
              </h4>
              <div className="flex items-center gap-4 mt-1 text-emerald-100">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {formatTime(currentShift.start_time)} - {formatTime(currentShift.end_time)}
                </span>
                {currentShift.shift_date && (
                  <span className="text-sm opacity-80">
                    {formatDate(currentShift.shift_date)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action bar */}
        <div className="px-4 py-2 bg-emerald-800/30 flex items-center justify-between">
          <span className="text-xs text-emerald-200">
            {shifts.length > 1 ? `+${shifts.length - 1} more shift${shifts.length > 2 ? 's' : ''}` : 'Tap to view details'}
          </span>
          <button
            onClick={handleViewShift}
            className="flex items-center gap-1 text-sm font-medium hover:underline"
            data-testid="view-shift-btn"
          >
            View Shift
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Progress bar for auto-dismiss */}
        <div className="h-1 bg-emerald-800/50">
          <div 
            className="h-full bg-white/50 animate-progress-bar"
            style={{ animationDuration: '10s' }}
          />
        </div>
      </div>

      <style>{`
        @keyframes progress-bar {
          from { width: 100%; }
          to { width: 0%; }
        }
        .animate-progress-bar {
          animation: progress-bar linear forwards;
        }
      `}</style>
    </div>
  );
}

export default NewShiftAlert;
